import { useCallback, useMemo, useState } from "react";
import { Filter } from "lucide-react";
import { startOfDay, endOfDay, format } from "date-fns";
import { Namespace, useDateFnsLocale, useTypedT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import {
  DashboardMap,
  type EnrichedPickupLocation,
  type FlyToRequest,
} from "../../dashboard/components/map.dashboard";
import { MapSearch } from "../../dashboard/components/map-search.dashboard";
import { useDashboardFilters } from "../../dashboard/lib/use-dashboard-filters.hook";
import { useWasteStatus } from "../../dashboard/lib/use-waste-status.hook";
import { useTrucksByProvider } from "../../dashboard/lib/use-trucks-by-provider.hook";
import { useMapTrucks } from "../../dashboard/lib/use-map-trucks.hook";
import { useMonitorStats } from "../lib/use-monitor-stats.hook";
import { useScheduleFulfillment } from "../lib/use-schedule-fulfillment.hook";
import { useAnomalyAnalytics } from "../lib/use-anomaly-analytics.hook";
import { MonitorSidebar } from "../components/monitor-sidebar";
import { ScheduleFulfillmentPanel } from "../components/schedule-fulfillment-panel";
import { GasAnalyzerPanel } from "../components/gas-analyzer-panel";
import { AnomalyAnalyticsPanel } from "../components/anomaly-analytics-panel";
import { FilterForm, type ExtraFilterItem } from "~/features/filter/filter.form";
import {
  useGetGovGeoAccessQuery,
  useTrucksQuery,
  useJobsQuery,
} from "~/shared/api/govDash/govDash.api";

type GovTruckInfo = ConnectProto.GovTruckInfo;
type GovGeoAccess = ConnectProto.GovGeoAccess;

export const MonitorPage = () => {
  const t = useTypedT(Namespace.Govdash);
  const dateFnsLocale = useDateFnsLocale();

  const { data: govGeoAccessResp } = useGetGovGeoAccessQuery({});
  const govGeoAccesses = useMemo(() => {
    return (govGeoAccessResp?.govGeoAccesses ||
      (govGeoAccessResp as Record<string, unknown>)?.gov_geo_accesses ||
      []) as GovGeoAccess[];
  }, [govGeoAccessResp]);

  const {
    extraFilters,
    setExtraFilters,
    govOrganizationQueryArg,
    showTrucks,
    showMvoTrucks,
    showGovernmentTrucks,
  } = useDashboardFilters(govGeoAccesses);

  const govOrganizationQueryArgBigInt = useMemo(
    () => ({
      districtOrganizationIds: (govOrganizationQueryArg.districtOrganizationIds ?? []).map((id) =>
        BigInt(id),
      ),
      serviceProviderIds: (govOrganizationQueryArg.serviceProviderIds ?? []).map((id) =>
        BigInt(id),
      ),
    }),
    [govOrganizationQueryArg],
  );

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const jobsTimeFilter = useMemo(
    () => ({
      from: { seconds: BigInt(Math.floor(startOfDay(selectedDate).getTime() / 1000)) },
      to: { seconds: BigInt(Math.floor(endOfDay(selectedDate).getTime() / 1000)) },
    }),
    [selectedDate],
  );

  const wasteStatus = useWasteStatus({
    districtOrganizationIds: govOrganizationQueryArg.districtOrganizationIds,
    serviceProviderIds: govOrganizationQueryArg.serviceProviderIds,
    selectedDate,
  });

  const truckProviderGroups = useTrucksByProvider({
    districtOrganizationIds: govOrganizationQueryArg.districtOrganizationIds,
    serviceProviderIds: govOrganizationQueryArg.serviceProviderIds,
  });
  const trucksOnLineTotal = useMemo(
    () => truckProviderGroups.reduce((sum, group) => sum + group.truckCount, 0),
    [truckProviderGroups],
  );

  // Unconditional (unlike the map's `trucks` below, which is skipped while
  // "show trucks" is off) so the online/total ratio stays correct regardless
  // of that toggle. Same query useTrucksByProvider makes internally, just also
  // needed here unfiltered for the fleet total.
  const { data: allTrucksResp } = useTrucksQuery(govOrganizationQueryArgBigInt);
  const trucksOnLinePercent = useMemo(() => {
    const total = allTrucksResp?.trucks?.length ?? 0;
    return total > 0 ? Number(((trucksOnLineTotal / total) * 100).toFixed(1)) : 0;
  }, [allTrucksResp, trucksOnLineTotal]);

  const monitorStats = useMonitorStats({
    districtOrganizationIds: govOrganizationQueryArgBigInt.districtOrganizationIds,
    serviceProviderIds: govOrganizationQueryArgBigInt.serviceProviderIds,
    selectedDate,
  });

  const { data: { jobs = [] } = {} } = useJobsQuery({
    ...govOrganizationQueryArgBigInt,
    timeFilter: jobsTimeFilter,
  });

  const { trucks, governmentTruckIds } = useMapTrucks({
    ...govOrganizationQueryArgBigInt,
    showMvoTrucks,
    showGovernmentTrucks,
  });

  // One marker per pickup location: group the selected day's jobs by
  // pickupLocation id, mirroring the dashboard's map (see dashboard.page.tsx
  // `pickupLocations`).
  const pickupLocations = useMemo(() => {
    const byId = new Map<string, EnrichedPickupLocation>();
    for (const job of jobs) {
      const pl = (job as any)?.pickupLocation;
      if (!pl || typeof pl.lat !== "number" || typeof pl.lon !== "number") continue;
      const key = String(pl.id);
      const existing = byId.get(key);
      if (existing) {
        existing._jobs.push(job);
      } else {
        byId.set(key, {
          ...(pl as EnrichedPickupLocation),
          _jobs: [job],
          _serviceProvider: job.serviceProvider ?? {},
        });
      }
    }
    return Array.from(byId.values());
  }, [jobs]);

  const scheduleFulfillment = useScheduleFulfillment(pickupLocations);

  const anomalyAnalytics = useAnomalyAnalytics({
    districtOrganizationIds: govOrganizationQueryArgBigInt.districtOrganizationIds,
    serviceProviderIds: govOrganizationQueryArgBigInt.serviceProviderIds,
    selectedDate,
  });

  const gasAnalyzerPeriodLabel = useMemo(() => {
    const monthYear = format(selectedDate, "LLLL yyyy", { locale: dateFnsLocale });
    return t("monitor.gasAnalyzerPeriod", {
      month: monthYear.charAt(0).toUpperCase() + monthYear.slice(1),
    });
  }, [selectedDate, dateFnsLocale, t]);

  // A fresh object per request, so selecting the same place twice flies again.
  const [flyTo, setFlyTo] = useState<FlyToRequest | null>(null);
  const handleFlyTo = useCallback((target: FlyToRequest) => {
    setFlyTo({ lat: target.lat, lon: target.lon });
  }, []);

  const [filterOpen, setFilterOpen] = useState(false);

  const handleFilterApply = (payload: {
    selectedTabKey: string;
    newSelected: ExtraFilterItem[];
  }) => {
    setExtraFilters(payload.newSelected);
    setFilterOpen(false);
  };

  return (
    <div className="flex h-full w-full flex-row overflow-hidden">
      <MonitorSidebar
        selectedDate={selectedDate}
        onSelectedDateChange={setSelectedDate}
        monitorStats={monitorStats}
        wasteStatus={wasteStatus}
        trucksOnLineTotal={trucksOnLineTotal}
        trucksOnLinePercent={trucksOnLinePercent}
        truckProviderGroups={truckProviderGroups}
      />

      <div className="min-h-0 flex-[5] pb-2 flex flex-col gap-2">
        <div className="flex-[7] flex flex-row gap-2">
          {/* <div className="flex flex-1"> */}
          <div className="flex-[7]">
            <DashboardMap
              pickupLocations={pickupLocations}
              trucks={trucks as GovTruckInfo[]}
              governmentTruckIds={governmentTruckIds}
              selection={{ type: "none" }}
              onPickupClick={() => {}}
              onTruckClick={() => {}}
              showTrucks={showTrucks}
              flyTo={flyTo}
            >
              <div className="absolute top-0 w-full z-[400] flex items-center justify-between gap-2 flex-wrap p-2 pointer-events-none">
                <div className="pointer-events-auto shrink-0">
                  <MapSearch
                    pickupLocations={pickupLocations}
                    trucks={trucks as GovTruckInfo[]}
                    disposalSites={[]}
                    depots={[]}
                    suspiciousLocations={[]}
                    showTrucks={showTrucks}
                    onPickupClick={() => {}}
                    onTruckClick={() => {}}
                    onFlyTo={handleFlyTo}
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0 pointer-events-auto">
                  <button
                    type="button"
                    aria-label={t("actions.openFilters")}
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center cursor-pointer p-0 hover:bg-slate-50 transition-colors"
                    onClick={() => setFilterOpen(true)}
                  >
                    <Filter className="w-5 h-5 fill-brand-blue" strokeWidth={0} />
                  </button>
                </div>
              </div>
            </DashboardMap>
          </div>
          <div className="flex min-h-0 flex-[2]">
            <AnomalyAnalyticsPanel
              days={anomalyAnalytics.days}
              maxDayCount={anomalyAnalytics.maxDayCount}
              topProviders={anomalyAnalytics.topProviders}
              otherProvidersCount={anomalyAnalytics.otherProvidersCount}
              monthPeriodLabel={gasAnalyzerPeriodLabel}
              dateFnsLocale={dateFnsLocale}
            />
          </div>
          {/* </div> */}
          {/* <div>pew</div> */}
        </div>
        <div className="flex min-h-0 flex-[2] gap-2 overflow-hidden">
          <ScheduleFulfillmentPanel
            fact={scheduleFulfillment.fact}
            plan={scheduleFulfillment.plan}
            notExported={scheduleFulfillment.notExported}
            totalLocations={scheduleFulfillment.totalLocations}
            wasteTons={monitorStats.wasteTons}
          />
          <GasAnalyzerPanel periodLabel={gasAnalyzerPeriodLabel} />
        </div>
      </div>

      <FilterForm
        open={filterOpen}
        onOpenChange={setFilterOpen}
        onApply={handleFilterApply}
        showFilterTabs={false}
        extraFilters={extraFilters}
      />
    </div>
  );
};
